import { useEffect, useMemo, useState } from 'react'

import { useMessages } from '@/modules/messages/hooks/useMessages'
import { DataTable } from '@/shared/components/data-table/DataTable'

import { purchaseColumns } from '../../constants/purchaseColumns.jsx'
import { usePurchase } from '../../hooks/usePurchase'

const PAGE_SIZE = 10

export function PurchaseTable({ onEdit, onDelete, onMessage }) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const { purchases, fetchPurchasePersons, isLoading } = usePurchase()
  const { unreadCounts } = useMessages()

  useEffect(() => {
    fetchPurchasePersons()
  }, [fetchPurchasePersons])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 300)

    return () => clearTimeout(timer)
  }, [search])

  const columns = useMemo(
    () => purchaseColumns(onEdit, onDelete, onMessage, unreadCounts?.bySender),
    [onEdit, onDelete, onMessage, unreadCounts?.bySender],
  )

  const filteredPurchases = useMemo(() => {
    if (!debouncedSearch) return purchases

    const query = debouncedSearch.toLowerCase()

    return purchases.filter((person) =>
      [person.name, person.email, person.phoneNumber].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(query),
      ),
    )
  }, [purchases, debouncedSearch])

  const paginatedPurchases = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE
    return filteredPurchases.slice(startIndex, startIndex + PAGE_SIZE)
  }, [filteredPurchases, page])

  return (
    <DataTable
      columns={columns}
      data={paginatedPurchases}
      getRowId={(row, index) => row._id || row.id || index}
      loading={isLoading}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search by name, email or phone number...',
      }}
      filterSummary={`${paginatedPurchases.length} of ${filteredPurchases.length}`}
      pagination={{
        page,
        pageSize: PAGE_SIZE,
        total: filteredPurchases.length,
        onPageChange: setPage,
      }}
    />
  )
}
